/**
 * Algolia adapter for React InstantSearch
 *
 * This file provides a lightweight Algolia search client
 * for use with react-instantsearch components.
 */

import { liteClient as createLiteClient } from 'algoliasearch/lite';

const ALGOLIA_APP_ID = import.meta.env.VITE_ALGOLIA_APP_ID;
const ALGOLIA_SEARCH_KEY = import.meta.env.VITE_ALGOLIA_SEARCH_KEY;

// Check if credentials are available
const hasCredentials = !!(ALGOLIA_APP_ID && ALGOLIA_SEARCH_KEY);

if (!hasCredentials) {
  console.warn('⚠️ Missing Algolia credentials. Algolia search will not work. Falling back to Typesense if available.');
}

/**
 * Lightweight Algolia search client for InstantSearch
 * Optimized for small bundle size and fast searches
 * Returns null if credentials are not available
 */
export const searchClient = hasCredentials
  ? createLiteClient(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
  : null;

/**
 * Algolia index names
 */
export const ALGOLIA_INDICES = {
  PRIMARY: 'sequences',
  CREATED_AT_DESC: 'sequences_created_at_desc',
  PRICE_ASC: 'sequences_price_asc',
  PRICE_DESC: 'sequences_price_desc',
} as const;

export type AlgoliaIndexName = typeof ALGOLIA_INDICES[keyof typeof ALGOLIA_INDICES];

/**
 * Get the appropriate index name based on sort option
 */
export function getAlgoliaIndexName(sortBy?: string): AlgoliaIndexName {
  switch (sortBy) {
    case 'created_at:desc':
    case 'sequences_v2/sort/created_at:desc':
      return ALGOLIA_INDICES.CREATED_AT_DESC;

    case 'price:asc':
    case 'sequences_v2/sort/price:asc':
      return ALGOLIA_INDICES.PRICE_ASC;

    case 'price:desc':
    case 'sequences_v2/sort/price:desc':
      return ALGOLIA_INDICES.PRICE_DESC;

    default:
      return ALGOLIA_INDICES.PRIMARY;
  }
}

/**
 * Transform Typesense facet name to Algolia attribute name
 * For compatibility with existing UI code
 */
export function transformFacetName(typesenseFacet: string): string {
  // Algolia uses the same attribute names, so no transformation needed
  return typesenseFacet;
}

/**
 * Transform Typesense sort option to Algolia index name
 */
export function transformSortOption(typesenseSort: string): string {
  // Map Typesense sort options to Algolia replica indices
  const mapping: Record<string, string> = {
    'sequences_v2': ALGOLIA_INDICES.PRIMARY,
    'sequences_v2/sort/download_count:desc': ALGOLIA_INDICES.PRIMARY, // Use primary with hot_score
    'sequences_v2/sort/created_at:desc': ALGOLIA_INDICES.CREATED_AT_DESC,
    'sequences_v2/sort/price:asc': ALGOLIA_INDICES.PRICE_ASC,
    'sequences_v2/sort/price:desc': ALGOLIA_INDICES.PRICE_DESC,
  };

  return mapping[typesenseSort] || ALGOLIA_INDICES.PRIMARY;
}
